import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getTourBySlug, createBooking } from '../services/api';
import { MapPin, Clock, Users, CheckCircle2, Calendar as CalendarIcon, ArrowLeft, MessageCircle, X, ChevronLeft, ChevronRight, CreditCard, Building2, Banknote, Plus, Trash2, Baby } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { format } from 'date-fns';
import { tr } from 'date-fns/locale';
import { toast } from 'sonner';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { TourSEO } from '../components/SEO';
import { useLanguage } from '../contexts/LanguageContext';
import { tourCurrencySymbol, getTourCurrency, parseISODate, toISODate, formatTourDate } from '@/lib/utils';
import { TourDepartures, TourDateCards } from '../components/TourDepartures';
import { RelatedTours } from '../components/RelatedTours';
import { trackEvent, getAnalyticsIds } from '@/lib/analytics';

const WHATSAPP_NUMBER = '905414112373';

const bookingSchema = z.object({
  first_name: z.string().min(2, 'Ad en az 2 karakter olmalıdır'),
  last_name: z.string().min(2, 'Soyad en az 2 karakter olmalıdır'),
  tc_number: z.string().length(11, 'TC Kimlik No 11 haneli olmalıdır').regex(/^\d+$/, 'TC Kimlik No sadece rakamlardan oluşmalıdır'),
  birth_date: z.string().min(1, 'Doğum tarihi gereklidir'),
  email: z.string().email('Geçerli bir e-posta adresi giriniz'),
  phone: z.string().min(10, 'Geçerli bir telefon numarası giriniz'),
  number_of_guests: z.number().min(1).max(40),
  special_requests: z.string().optional(),
  payment_method: z.enum(['credit_card', 'bank_transfer', 'cash_on_tour']),
});

export default function TourDetailPage() {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [tour, setTour] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  
  // Konaklamalı tur için oda tipi ve çocuk bilgileri
  const [roomType, setRoomType] = useState('double');
  const [adults, setAdults] = useState(2);
  const [children, setChildren] = useState([]);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    watch,
  } = useForm({
    resolver: zodResolver(bookingSchema),
    defaultValues: {
      number_of_guests: 1,
      payment_method: 'credit_card',
    },
  });

  const numberOfGuests = watch('number_of_guests');
  const paymentMethod = watch('payment_method');
  
  // Çocuk ekleme/çıkarma fonksiyonları
  const addChild = () => {
    setChildren([...children, { age: 0 }]);
  };
  
  const removeChild = (index) => {
    setChildren(children.filter((_, i) => i !== index));
  };
  
  const updateChildAge = (index, age) => {
    const newChildren = [...children];
    newChildren[index].age = parseInt(age);
    setChildren(newChildren);
  };
  
  // Fiyat hesaplama (konaklamalı turlar için)
  const calculateTotalPrice = () => {
    if (!tour) return 0;
    
    const isAccommodationTour = tour.tour_type === 'yurtici' || tour.tour_type === 'yurtdisi';
    
    if (!isAccommodationTour) {
      // Günübirlik tur - basit hesaplama
      return tour.price * numberOfGuests;
    }
    
    // Konaklamalı tur fiyat hesabı
    let total = 0;
    
    // Yetişkin fiyatları
    if (roomType === 'single') {
      total = tour.price + (tour.price_single || 0); // DB fiyat + single fark
    } else if (roomType === 'triple') {
      total = (tour.price_triple || tour.price) * adults;
    } else {
      // Double
      total = tour.price * adults;
    }
    
    // Çocuk fiyatları
    children.forEach(child => {
      const childPrice = tour.child_prices?.find(cp => 
        child.age >= cp.age_min && child.age <= cp.age_max
      );
      total += childPrice?.price || 0;
    });
    
    return total;
  };

  // Generate gallery images (main + additional if available)
  const galleryImages = tour ? [
    tour.image_url,
    ...(tour.gallery_images || [])
  ].filter(Boolean) : [];

  const fetchTour = async () => {
    try {
      const response = await getTourBySlug(slug, language);
      setTour(response.data);
    } catch (error) {
      console.error('Error fetching tour:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedDate(null);
    fetchTour();
  }, [slug, language]); // eslint-disable-line react-hooks/exhaustive-deps

  const departures = tour?.departures || [];
  const hasBookableDate = departures.some(d => d.bookable);
  const selectedDateStr = selectedDate ? toISODate(selectedDate) : null;
  const [bookingStarted, setBookingStarted] = useState(false);
  useEffect(() => { setBookingStarted(false); }, [slug]);
  const markBookingStart = () => {
    if (bookingStarted || !tour) return;
    setBookingStarted(true);
    trackEvent('booking_start', { tour_slug: tour.slug });
  };
  const selectDate = (d) => { markBookingStart(); setSelectedDate(parseISODate(d)); };
  const programBaseDate = selectedDateStr || tour?.next_departure;
  const programDayDate = (dayNumber) => {
    if (!programBaseDate) return null;
    const d = parseISODate(programBaseDate);
    d.setDate(d.getDate() + (Number(dayNumber) || 1) - 1);
    return formatTourDate(toISODate(d), language);
  };

  const onSubmit = async (data) => {
    if (!selectedDate) {
      toast.error('Lütfen bir tarih seçiniz');
      return;
    }

    setSubmitting(true);
    try {
      const isAccommodationTour = tour.tour_type === 'yurtici' || tour.tour_type === 'yurtdisi';
      const currency = tourCurrencySymbol(tour);
      const currencyCode = getTourCurrency(tour);
      const totalPrice = isAccommodationTour ? calculateTotalPrice() : tour.price * data.number_of_guests;
      
      const paymentLabels = {
        credit_card: 'Kredi Kartı',
        bank_transfer: 'Havale/EFT',
        cash_on_tour: 'Tur Esnasında Ödeme'
      };
      
      const roomTypeLabels = {
        single: 'Single (Tek Kişilik)',
        double: 'Double (2 Kişilik)',
        triple: 'Triple (3 Kişilik)'
      };
      
      const bookingData = {
        ...getAnalyticsIds(),
        tour_id: tour.id,
        tour_title: tour.title,
        tour_type: tour.tour_type || 'istanbul',
        ...data,
        selected_date: format(selectedDate, 'yyyy-MM-dd'),
        total_price: totalPrice,
        currency: currencyCode,
        // Konaklamalı tur bilgileri
        room_type: isAccommodationTour ? roomType : null,
        adults: isAccommodationTour ? adults : null,
        children: isAccommodationTour ? children : [],
      };

      await createBooking(bookingData);
      
      // WhatsApp Profesyonel Voucher Mesajı
      let voucherMessage = '';
      
      if (isAccommodationTour) {
        // Konaklamalı Tur Voucher
        voucherMessage = `━━━━━━━━━━━━━━━━━━━━
🎫 *REZERVASYON ONAYI*
━━━━━━━━━━━━━━━━━━━━

🌍 *${tour.title}*

📋 *REZERVASYON BİLGİLERİ*
├ 👤 Misafir: ${data.first_name} ${data.last_name}
├ 🪪 TC: ${data.tc_number}
├ 📧 E-posta: ${data.email}
├ 📱 Telefon: ${data.phone}

🏨 *KONAKLAMA BİLGİLERİ*
├ 🛏️ Oda Tipi: ${roomTypeLabels[roomType]}
├ 👥 Yetişkin: ${adults} kişi
${children.length > 0 ? `├ 👶 Çocuk: ${children.length} kişi (${children.map(c => c.age + ' yaş').join(', ')})` : ''}

📅 *TUR TARİHİ*
├ 🗓️ ${format(selectedDate, 'dd MMMM yyyy', { locale: tr })}
├ ⏱️ Süre: ${tour.duration}

💰 *ÖDEME BİLGİLERİ*
├ 💳 Yöntem: ${paymentLabels[data.payment_method]}
├ 💵 Toplam: *${currency}${totalPrice}*

${data.special_requests ? `📝 *ÖZEL İSTEKLER*\n${data.special_requests}\n` : ''}
━━━━━━━━━━━━━━━━━━━━
✅ Rezervasyonunuz alınmıştır!
📞 Onay için sizinle iletişime geçeceğiz.
━━━━━━━━━━━━━━━━━━━━`;
      } else {
        // Günübirlik Tur Voucher
        voucherMessage = `━━━━━━━━━━━━━━━━━━━━
🎫 *REZERVASYON ONAYI*
━━━━━━━━━━━━━━━━━━━━

🚶 *${tour.title}*

📋 *MİSAFİR BİLGİLERİ*
├ 👤 Ad Soyad: ${data.first_name} ${data.last_name}
├ 🪪 TC: ${data.tc_number}
├ 📧 E-posta: ${data.email}
├ 📱 Telefon: ${data.phone}

📅 *TUR DETAYLARI*
├ 🗓️ Tarih: ${format(selectedDate, 'dd MMMM yyyy', { locale: tr })}
├ 👥 Kişi Sayısı: ${data.number_of_guests}
├ ⏱️ Süre: ${tour.duration}
├ 📍 Buluşma: ${tour.meeting_point || 'Belirtilecek'}

💰 *ÖDEME*
├ 💳 Yöntem: ${paymentLabels[data.payment_method]}
├ 💵 Toplam: *${currency}${totalPrice}*

${data.special_requests ? `📝 *ÖZEL İSTEKLER*\n${data.special_requests}\n` : ''}
━━━━━━━━━━━━━━━━━━━━
✅ Rezervasyonunuz alınmıştır!
━━━━━━━━━━━━━━━━━━━━`;
      }

      const whatsappUrl = `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(voucherMessage)}`;
      
      // Başarı bildirimi göster
      toast.success(
        <div className="text-center">
          <div className="text-2xl mb-2">🎉</div>
          <p className="font-bold text-lg">Rezervasyonunuz Alındı!</p>
          <p className="text-sm mt-1 text-gray-600">WhatsApp ile onay almak için yönlendiriliyorsunuz...</p>
        </div>,
        { duration: 4000 }
      );
      
      // 1.5 saniye sonra otomatik olarak WhatsApp'ı aç
      setTimeout(() => {
        window.open(whatsappUrl, '_blank');
      }, 1500);
      
      // Reset form
      setSelectedDate(null);
      setChildren([]);
      setAdults(2);
      setRoomType('double');
      fetchTour();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Rezervasyon oluşturulurken bir hata oluştu. Lütfen tekrar deneyiniz.');
      console.error('Error creating booking:', error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yüklüyor...</p>
        </div>
      </div>
    );
  }

  if (!tour) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Tur bulunamadı</h2>
          <Link to="/turlar">
            <Button>Turlara Dön</Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <TourSEO tour={tour} />
      
      {/* Back Button */}
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 py-8">
        <Link
          to="/turlar"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-orange-500 transition-colors"
          data-testid="back-to-tours"
        >
          <ArrowLeft size={20} />
          <span>Turlara Dön</span>
        </Link>
      </div>

      {/* Hero Image with Gallery */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-8">
        <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden group cursor-pointer" onClick={() => { setLightboxIndex(0); setLightboxOpen(true); }}>
          <img 
            src={tour.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'} 
            alt={tour.title} 
            className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
            }}
          />
          <div className="absolute inset-0 bg-black/20 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
            <span className="bg-white/90 px-4 py-2 rounded-full text-sm font-bold">Galeriyi Görüntüle ({galleryImages.length} Fotoğraf)</span>
          </div>
          {tour.discount_percentage > 0 && (
            <div className="absolute top-6 right-6 bg-red-500 text-white px-4 py-2 rounded-full text-lg font-ui font-bold">
              %{tour.discount_percentage} İndirim
            </div>
          )}
          {/* WhatsApp Floating Button */}
          <a
            href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Merhaba, "${tour.title}" turu hakkında bilgi almak istiyorum.`)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="absolute bottom-6 right-6 bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-full flex items-center gap-2 shadow-lg transition-all hover:scale-105"
            data-testid="tour-whatsapp-btn"
            onClick={(e) => e.stopPropagation()}
          >
            <MessageCircle size={20} />
            <span className="font-bold">WhatsApp ile Sor</span>
          </a>
        </div>
        
        {/* Gallery Thumbnails */}
        {galleryImages.length > 1 && (
          <div className="flex gap-2 mt-4 overflow-x-auto pb-2">
            {galleryImages.map((img, idx) => (
              <button
                key={idx}
                onClick={() => { setLightboxIndex(idx); setLightboxOpen(true); }}
                className={`flex-shrink-0 w-20 h-20 rounded-lg overflow-hidden border-2 transition-all ${idx === 0 ? 'border-orange-500' : 'border-transparent hover:border-orange-300'}`}
              >
                <img src={img} alt={`Galeri ${idx + 1}`} className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        )}
      </section>

      {/* Lightbox */}
      {lightboxOpen && (
        <div className="fixed inset-0 bg-black/95 z-50 flex items-center justify-center" onClick={() => setLightboxOpen(false)}>
          <button 
            className="absolute top-4 right-4 text-white hover:text-orange-500 transition-colors"
            onClick={() => setLightboxOpen(false)}
          >
            <X size={32} />
          </button>
          
          {galleryImages.length > 1 && (
            <>
              <button 
                className="absolute left-4 text-white hover:text-orange-500 transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev - 1 + galleryImages.length) % galleryImages.length); }}
              >
                <ChevronLeft size={48} />
              </button>
              <button 
                className="absolute right-4 text-white hover:text-orange-500 transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); setLightboxIndex((prev) => (prev + 1) % galleryImages.length); }}
              >
                <ChevronRight size={48} />
              </button>
            </>
          )}
          
          <img 
            src={galleryImages[lightboxIndex]} 
            alt={`${tour.title} - ${lightboxIndex + 1}`}
            className="max-h-[90vh] max-w-[90vw] object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          
          <div className="absolute bottom-4 text-white text-sm">
            {lightboxIndex + 1} / {galleryImages.length}
          </div>
        </div>
      )}

      {/* Content */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left Column - Tour Details */}
          <div className="lg:col-span-2 space-y-8">
            <div>
              <div className="flex items-center space-x-3 text-sm text-gray-500 mb-4">
                <MapPin size={18} />
                <span>{tour.category}</span>
                <span className="text-gray-300">•</span>
                <Clock size={18} />
                <span>{tour.duration}</span>
                <span className="text-gray-300">•</span>
                <Users size={18} />
                <span>Max {tour.max_capacity} kişi</span>
              </div>
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4" data-testid="tour-title">
                {tour.title}
              </h1>
              <p className="text-xl text-gray-600">{tour.short_description}</p>
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Açıklama</h2>
              {programDayDate(1) && (
                <p className="inline-flex items-center gap-2 mb-4 rounded-full bg-orange-50 px-4 py-2 text-sm font-bold text-orange-600 capitalize" data-testid="tour-description-date">
                  <CalendarIcon size={16} /> {t('tour.dates_section')}: {programDayDate(1)}
                </p>
              )}
              <p className="text-gray-600 leading-relaxed whitespace-pre-line">{tour.description}</p>
            </div>

            {tour.highlights && tour.highlights.length > 0 && (
              <div className="bg-white rounded-2xl p-8 border border-gray-100">
                <h2 className="text-2xl font-bold text-gray-900 mb-4">Öne Çıkanlar</h2>
                <ul className="space-y-3">
                  {tour.highlights.map((highlight, index) => (
                    <li key={index} className="flex items-start space-x-3">
                      <CheckCircle2 className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                      <span className="text-gray-600">{highlight}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <TourDateCards departures={departures} selectedDate={selectedDateStr} onSelect={selectDate} />

            {/* Tur Programı */}
            {tour.program && tour.program.length > 0 && (
              <div className="bg-white rounded-2xl p-8 border border-gray-100" data-testid="tour-program-section">
                <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('tours.program')}</h2>
                <div className="space-y-6">
                  {/* Sort by day number and display in order */}
                  {[...tour.program]
                    .sort((a, b) => (a.day || 0) - (b.day || 0))
                    .map((day, dayIndex) => (
                    <div key={dayIndex} className="border-l-4 border-orange-500 pl-6 py-4 bg-gray-50 rounded-r-lg" data-testid={`program-day-${day.day}`}>
                      <div className="flex items-start gap-3 mb-3">
                        <span className="bg-orange-500 text-white px-4 py-2 rounded-full text-sm font-bold whitespace-nowrap">
                          {language === 'tr' ? `${day.day}. ${t('tours.day')}` : `${t('tours.day')} ${day.day}`}
                        </span>
                        <div className="pt-1">
                          <h3 className="text-xl font-bold text-gray-900">{day.title}</h3>
                          {programDayDate(day.day) && (
                            <p className="text-sm font-semibold text-orange-600 mt-1 capitalize" data-testid={`program-day-${day.day}-date`}>{programDayDate(day.day)}</p>
                          )}
                        </div>
                      </div>
                      
                      {day.description && (
                        <p className="text-gray-600 mb-4 leading-relaxed whitespace-pre-line">{day.description}</p>
                      )}

                      {!day.description && day.activities?.length > 0 && (
                        <p className="text-gray-600 mb-4 leading-relaxed" data-testid={`program-day-${day.day}-narrative`}>
                          {day.activities.map(act => (act.activity || '').trim().replace(/[.。]$/, '')).filter(Boolean).join('. ')}.
                        </p>
                      )}
                      
                      <div className="flex flex-wrap gap-4 text-sm">
                        {day.time && (
                          <span className="flex items-center gap-2 bg-blue-50 text-blue-700 px-3 py-1 rounded-full">
                            <Clock size={14} />
                            {day.time}
                          </span>
                        )}
                        {day.location && (
                          <span className="flex items-center gap-2 bg-green-50 text-green-700 px-3 py-1 rounded-full">
                            <MapPin size={14} />
                            {day.location}
                          </span>
                        )}
                        {day.meals && (
                          <span className="flex items-center gap-2 bg-amber-50 text-amber-700 px-3 py-1 rounded-full">
                            🍽️ {day.meals}
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {tour.included.length > 0 && (
                <div className="bg-white rounded-2xl p-8 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{t('tours.included')}</h2>
                  <ul className="space-y-2">
                    {tour.included.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                        <CheckCircle2 className="text-green-500 flex-shrink-0 mt-0.5" size={16} />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {tour.excluded.length > 0 && (
                <div className="bg-white rounded-2xl p-8 border border-gray-100">
                  <h2 className="text-xl font-bold text-gray-900 mb-4">{t('tours.excluded')}</h2>
                  <ul className="space-y-2">
                    {tour.excluded.map((item, index) => (
                      <li key={index} className="flex items-start space-x-2 text-sm text-gray-600">
                        <span className="text-red-500 flex-shrink-0">✗</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="bg-white rounded-2xl p-8 border border-gray-100">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Buluşma Noktası</h2>
              <p className="text-gray-600">{tour.meeting_point}</p>
            </div>
          </div>

          {/* Right Column - Booking Form */}
          <div>
            <div className="bg-white rounded-2xl p-8 border border-gray-100 sticky top-24">
              {/* Fiyat Bölümü - Konaklamalı ve Günübirlik Turlar için Farklı */}
              {(tour.tour_type === 'yurtici' || tour.tour_type === 'yurtdisi') && (tour.price_single || tour.price_triple || (tour.child_prices && tour.child_prices.length > 0)) ? (
                // Konaklamalı Tur Fiyat Tablosu
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-gray-900 mb-3">Fiyat Tablosu</h3>
                  <div className="bg-gray-50 rounded-xl p-4 space-y-3">
                    {/* DB Fiyat - Ana Fiyat */}
                    <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                      <div>
                        <span className="font-semibold text-gray-900">DB Kişi Başı</span>
                        <p className="text-xs text-gray-500">Double (2 kişilik oda)</p>
                      </div>
                      <span className="text-2xl font-bold text-orange-500 font-ui">
                        {tourCurrencySymbol(tour)}{tour.price}
                      </span>
                    </div>
                    
                    {/* Single Oda Farkı */}
                    {tour.price_single && (
                      <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                        <div>
                          <span className="font-semibold text-gray-900">Single Oda Farkı</span>
                          <p className="text-xs text-gray-500">Tek kişilik oda ek ücreti</p>
                        </div>
                        <span className="text-lg font-bold text-blue-600 font-ui">
                          +{tourCurrencySymbol(tour)}{tour.price_single}
                        </span>
                      </div>
                    )}
                    
                    {/* Triple Fiyat */}
                    {tour.price_triple && (
                      <div className="flex justify-between items-center pb-3 border-b border-gray-200">
                        <div>
                          <span className="font-semibold text-gray-900">Triple Kişi Başı</span>
                          <p className="text-xs text-gray-500">3 kişilik oda</p>
                        </div>
                        <span className="text-lg font-bold text-green-600 font-ui">
                          {tourCurrencySymbol(tour)}{tour.price_triple}
                        </span>
                      </div>
                    )}
                    
                    {/* Çocuk Fiyatları */}
                    {tour.child_prices && tour.child_prices.length > 0 && (
                      <div className="pt-2">
                        <p className="text-sm font-semibold text-gray-700 mb-2">Çocuk Fiyatları:</p>
                        <div className="space-y-2">
                          {tour.child_prices.map((cp, idx) => (
                            <div key={idx} className="flex justify-between items-center text-sm">
                              <span className="text-gray-600">{cp.label || `${cp.age_min}-${cp.age_max} Yaş`}</span>
                              <span className="font-semibold text-purple-600">
                                {cp.price === 0 ? 'Ücretsiz' : `${tourCurrencySymbol(tour)}${cp.price}`}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                // Günübirlik Tur - Basit Fiyat
                <div className="mb-6">
                  <div className="text-3xl font-bold font-ui text-orange-500 mb-1" data-testid="tour-price">
                    {tourCurrencySymbol(tour)}{tour.price}
                    <span className="text-base text-gray-500 font-normal ml-1">{t('tours.per_person')}</span>
                  </div>
                  {numberOfGuests > 1 && (
                    <div className="text-sm text-gray-600">
                      Toplam: {tourCurrencySymbol(tour)}{tour.price * numberOfGuests}
                    </div>
                  )}
                </div>
              )}

              <form onSubmit={handleSubmit(onSubmit)} className="space-y-4" data-testid="booking-form">
                <div>
                  <Label htmlFor="first_name">{t('booking.first_name')} *</Label>
                  <Input
                    id="first_name"
                    {...register('first_name')}
                    placeholder="Adınız"
                    data-testid="booking-first-name"
                  />
                  {errors.first_name && <p className="text-sm text-red-500 mt-1">{errors.first_name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="last_name">{t('booking.last_name')} *</Label>
                  <Input
                    id="last_name"
                    {...register('last_name')}
                    placeholder="Soyadınız"
                    data-testid="booking-last-name"
                  />
                  {errors.last_name && <p className="text-sm text-red-500 mt-1">{errors.last_name.message}</p>}
                </div>

                <div>
                  <Label htmlFor="tc_number">{t('booking.tc_number')} *</Label>
                  <Input
                    id="tc_number"
                    {...register('tc_number')}
                    placeholder="12345678901"
                    maxLength={11}
                    data-testid="booking-tc-number"
                  />
                  {errors.tc_number && <p className="text-sm text-red-500 mt-1">{errors.tc_number.message}</p>}
                </div>

                <div>
                  <Label htmlFor="birth_date">{t('booking.birth_date')} *</Label>
                  <Input
                    id="birth_date"
                    type="date"
                    {...register('birth_date')}
                    data-testid="booking-birth-date"
                  />
                  {errors.birth_date && <p className="text-sm text-red-500 mt-1">{errors.birth_date.message}</p>}
                </div>

                <div>
                  <Label htmlFor="email">{t('booking.email')} *</Label>
                  <Input
                    id="email"
                    type="email"
                    {...register('email')}
                    placeholder="ornek@email.com"
                    data-testid="booking-email"
                  />
                  {errors.email && <p className="text-sm text-red-500 mt-1">{errors.email.message}</p>}
                </div>

                <div>
                  <Label htmlFor="phone">{t('booking.phone')} *</Label>
                  <Input
                    id="phone"
                    {...register('phone')}
                    placeholder="+90 5XX XXX XX XX"
                    data-testid="booking-phone"
                  />
                  {errors.phone && <p className="text-sm text-red-500 mt-1">{errors.phone.message}</p>}
                </div>

                {/* Konaklamalı Turlar için Oda Tipi ve Kişi Seçimi */}
                {(tour.tour_type === 'yurtici' || tour.tour_type === 'yurtdisi') && (tour.price_single || tour.price_triple || (tour.child_prices && tour.child_prices.length > 0)) && (
                  <div className="bg-blue-50 p-4 rounded-xl space-y-4 border border-blue-200">
                    <h4 className="font-bold text-blue-800 flex items-center gap-2">
                      <Users size={18} />
                      Konaklama Bilgileri
                    </h4>
                    
                    {/* Oda Tipi Seçimi */}
                    <div>
                      <Label className="text-blue-700">Oda Tipi</Label>
                      <Select value={roomType} onValueChange={setRoomType}>
                        <SelectTrigger className="bg-white h-auto min-h-10 [&>span]:line-clamp-none" data-testid="booking-room-type-select">
                          <SelectValue placeholder="Oda tipi seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="double">
                            <div className="flex justify-between items-center w-full">
                              <span>Double (2 Kişilik Oda)</span>
                              <span className="text-orange-600 font-bold ml-4">{tourCurrencySymbol(tour)}{tour.price}/kişi</span>
                            </div>
                          </SelectItem>
                          {tour.price_single && (
                            <SelectItem value="single">
                              <div className="flex justify-between items-center w-full">
                                <span>Single (Tek Kişilik Oda)</span>
                                <span className="text-blue-600 font-bold ml-4">+{tourCurrencySymbol(tour)}{tour.price_single} fark</span>
                              </div>
                            </SelectItem>
                          )}
                          {tour.price_triple && (
                            <SelectItem value="triple">
                              <div className="flex justify-between items-center w-full">
                                <span>Triple (3 Kişilik Oda)</span>
                                <span className="text-green-600 font-bold ml-4">{tourCurrencySymbol(tour)}{tour.price_triple}/kişi</span>
                              </div>
                            </SelectItem>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Yetişkin Sayısı */}
                    <div>
                      <Label className="text-blue-700">Yetişkin Sayısı</Label>
                      <Select value={adults.toString()} onValueChange={(v) => setAdults(parseInt(v))}>
                        <SelectTrigger className="bg-white">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {roomType === 'single' ? (
                            <SelectItem value="1">1 Yetişkin</SelectItem>
                          ) : roomType === 'triple' ? (
                            <>
                              <SelectItem value="2">2 Yetişkin</SelectItem>
                              <SelectItem value="3">3 Yetişkin</SelectItem>
                            </>
                          ) : (
                            <>
                              <SelectItem value="1">1 Yetişkin</SelectItem>
                              <SelectItem value="2">2 Yetişkin</SelectItem>
                            </>
                          )}
                        </SelectContent>
                      </Select>
                    </div>
                    
                    {/* Çocuk Ekleme */}
                    {tour.child_prices && tour.child_prices.length > 0 && (
                      <div>
                        <div className="flex justify-between items-center mb-2">
                          <Label className="text-blue-700">Çocuklar</Label>
                          <Button
                            type="button"
                            onClick={addChild}
                            variant="outline"
                            size="sm"
                            className="border-blue-300 text-blue-600 hover:bg-blue-100"
                          >
                            <Plus size={14} className="mr-1" /> Çocuk Ekle
                          </Button>
                        </div>
                        
                        {children.length === 0 ? (
                          <p className="text-sm text-gray-500 italic">Çocuk eklenmedi</p>
                        ) : (
                          <div className="space-y-2">
                            {children.map((child, index) => {
                              const childPrice = tour.child_prices.find(cp => 
                                child.age >= cp.age_min && child.age <= cp.age_max
                              );
                              return (
                                <div key={index} className="flex items-center gap-2 bg-white p-2 rounded border">
                                  <Baby size={18} className="text-purple-500" />
                                  <span className="text-sm font-medium">Çocuk {index + 1}:</span>
                                  <Select value={child.age.toString()} onValueChange={(v) => updateChildAge(index, v)}>
                                    <SelectTrigger className="w-24">
                                      <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                      {[...Array(18)].map((_, i) => (
                                        <SelectItem key={i} value={i.toString()}>{i} yaş</SelectItem>
                                      ))}
                                    </SelectContent>
                                  </Select>
                                  <span className="text-sm text-purple-600 font-semibold flex-1 text-right">
                                    {childPrice?.price === 0 ? 'Ücretsiz' : `${tourCurrencySymbol(tour)}${childPrice?.price || 0}`}
                                  </span>
                                  <Button
                                    type="button"
                                    onClick={() => removeChild(index)}
                                    variant="ghost"
                                    size="sm"
                                    className="text-red-500 hover:text-red-700 p-1"
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Toplam Fiyat Gösterimi */}
                    <div className="bg-white p-3 rounded-lg border-2 border-blue-300">
                      <div className="flex justify-between items-center">
                        <span className="font-semibold text-gray-700">Toplam Tutar:</span>
                        <span className="text-2xl font-bold text-orange-500">
                          {tourCurrencySymbol(tour)}{calculateTotalPrice()}
                        </span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Günübirlik turlar için basit kişi sayısı */}
                {!(tour.tour_type === 'yurtici' || tour.tour_type === 'yurtdisi') || !(tour.price_single || tour.price_triple || (tour.child_prices && tour.child_prices.length > 0)) ? (
                  <div>
                    <Label htmlFor="number_of_guests">{t('booking.guests')} *</Label>
                    <Input
                      id="number_of_guests"
                      type="number"
                      min="1"
                      max={tour.max_capacity}
                      {...register('number_of_guests', { valueAsNumber: true })}
                      data-testid="booking-guests"
                    />
                    {errors.number_of_guests && (
                      <p className="text-sm text-red-500 mt-1">{errors.number_of_guests.message}</p>
                    )}
                  </div>
                ) : null}

                <div>
                  <Label className="mb-2 block">{t('booking.date')} *</Label>
                  <TourDepartures departures={departures} selectedDate={selectedDateStr} onSelect={selectDate} infoRequestUrl={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Merhaba, "${tour.title}" turunun yeni tarihleri hakkında bilgi almak istiyorum.`)}`} />
                </div>

                <div>
                  <Label htmlFor="special_requests">{t('booking.special_requests')}</Label>
                  <textarea
                    id="special_requests"
                    {...register('special_requests')}
                    placeholder="Varsa özel isteğinizi belirtiniz..."
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500"
                    data-testid="booking-special-requests"
                  />
                </div>

                {/* Payment Method Selection */}
                <div>
                  <Label className="mb-3 block">{t('booking.payment_method')} *</Label>
                  <RadioGroup
                    defaultValue="credit_card"
                    onValueChange={(value) => setValue('payment_method', value)}
                    className="space-y-3"
                  >
                    <div className={`flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === 'credit_card' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                      <RadioGroupItem value="credit_card" id="credit_card" />
                      <Label htmlFor="credit_card" className="flex items-center gap-2 cursor-pointer flex-1">
                        <CreditCard size={20} className="text-orange-500" />
                        <div>
                          <div className="font-bold">{t('booking.credit_card')}</div>
                          <div className="text-xs text-gray-500">Güvenli online ödeme</div>
                        </div>
                      </Label>
                    </div>
                    
                    <div className={`flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === 'bank_transfer' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                      <RadioGroupItem value="bank_transfer" id="bank_transfer" />
                      <Label htmlFor="bank_transfer" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Building2 size={20} className="text-blue-500" />
                        <div>
                          <div className="font-bold">{t('booking.bank_transfer')}</div>
                          <div className="text-xs text-gray-500">Banka hesabına transfer</div>
                        </div>
                      </Label>
                    </div>
                    
                    <div className={`flex items-center space-x-3 p-4 border rounded-xl cursor-pointer transition-all ${paymentMethod === 'cash_on_tour' ? 'border-orange-500 bg-orange-50' : 'border-gray-200 hover:border-orange-300'}`}>
                      <RadioGroupItem value="cash_on_tour" id="cash_on_tour" />
                      <Label htmlFor="cash_on_tour" className="flex items-center gap-2 cursor-pointer flex-1">
                        <Banknote size={20} className="text-green-500" />
                        <div>
                          <div className="font-bold">{t('booking.cash_on_tour')}</div>
                          <div className="text-xs text-gray-500">Nakit veya kart ile yerinde ödeme</div>
                        </div>
                      </Label>
                    </div>
                  </RadioGroup>
                  {errors.payment_method && <p className="text-sm text-red-500 mt-1">{errors.payment_method.message}</p>}
                </div>

                <Button
                  type="submit"
                  disabled={submitting || !hasBookableDate}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-6 rounded-xl font-ui font-bold text-lg"
                  data-testid="booking-submit-button"
                >
                  {submitting ? '...' : t('booking.submit')}
                </Button>
                
                {/* WhatsApp Alternative */}
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(`Merhaba, "${tour.title}" turu için ${numberOfGuests} kişilik rezervasyon yapmak istiyorum.`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="w-full flex items-center justify-center gap-2 bg-green-500 hover:bg-green-600 text-white py-4 rounded-xl font-ui font-bold transition-all"
                  data-testid="booking-whatsapp-btn"
                >
                  <MessageCircle size={20} />
                  WhatsApp ile Rezervasyon
                </a>
              </form>
            </div>
          </div>
        </div>
      </section>
      <RelatedTours slug={slug} />
    </div>
  );
}
